/**
 * Client for the passes of the engine (CU-869fhuhy6, § 4.5).
 *
 * `GET /api/repricing/executions` replaces `scheduleApi.executions`: a
 * pass is asked for by strategies now, not by a schedule rule, and each
 * run carries `strategies: [{ id, name, composed, shared }]`, `pass_key`
 * and — for the passes before the migration — `rule_name` with
 * `legacy: true`, so the historical rows keep naming the rule as text
 * (criterion 41) once the rules table is gone.
 */
import { BACKEND_APIS } from '@constants/routes'
import { makeClient } from 'Pages/DsRepricing/@services/http'

export { ApiError } from 'Pages/DsRepricing/@services/http'

export const API = BACKEND_APIS.GENERAL_REPRICING_API

const http = makeClient(API)

const qs = (params) => {
	const q = new URLSearchParams()
	for (const [k, v] of Object.entries(params || {})) if (v != null && v !== '') q.set(k, String(v))
	const s = q.toString()
	return s ? `?${s}` : ''
}

export const executionsApi = {
	/** Newest first. `strategyId` narrows to the passes that strategy asked
	 *  for. `progress_ok: false` means the search counts were too slow to
	 *  wait for and every `searches` is null — see the webback `executions`
	 *  service. */
	list: ({ limit = 30, strategyId = null, executionId = null } = {}) =>
		http.get(`/executions${qs({ limit, strategy_id: strategyId, execution_id: executionId })}`),
	/** The searches of one pass (CU-869fmm943): one item per search it
	 *  launched, with what it carried, its PMS push and what the engine did
	 *  with it. `details_ok` / `events_ok` false when a half could not be
	 *  read; 404 for a pass that is not this tenant's. */
	searches: (executionId) => http.get(`/executions/${encodeURIComponent(executionId)}/searches`),
	/** `{ pass: { execution_id, slot } | null }` — the pass that launched a
	 *  search, for Real time's mark. Behind `ds_repricing`: never call it
	 *  without the feature (decision A). */
	passOfSearch: (idSearch) => http.get(`/searches/${encodeURIComponent(idSearch)}/pass`),
	/** What ran on each day of a range, folded. Days are UTC. */
	byDay: (fromIso, toIso) => http.get(`/executions/by-day${qs({ from: fromIso, to: toIso })}`),
	/** "Push now": send the ladders of every search of this pass that is
	 *  still waiting, and resend the ones the PMS refused. Audited as
	 *  `force_flush`; 409 when nothing is pending. */
	flush: (executionId, reason) =>
		http.post(`/executions/${encodeURIComponent(executionId)}/flush`, { reason: reason || null }),
	/** Launch a pass of every strategy that is on, now (criterion 26). */
	runNow: (reason) => http.post('/executions/run-now', { reason: reason || null }),
}
